import { PageTypePortal } from "@/lib/types";
import { parseClassIdFromRegistrationPath } from "@/lib/utils/classRegistrationRedirect";
import {
  getConfiguredAdminOrigin,
  getConfiguredPortalOrigin,
  isAdminHostname,
  isPortalHostname,
} from "./app-origin";

function isTrustedAuthCallbackHostname(hostname: string): boolean {
  const host = hostname.toLowerCase();

  if (isPortalHostname(host) || isAdminHostname(host)) {
    return true;
  }

  if (host === "localhost" || host === "127.0.0.1") {
    return true;
  }

  const portalHost = getConfiguredPortalOrigin()
    ? new URL(getConfiguredPortalOrigin()!).hostname
    : null;
  const adminHost = getConfiguredAdminOrigin()
    ? new URL(getConfiguredAdminOrigin()!).hostname
    : null;

  return host === portalHost || host === adminHost;
}

function isSafeInternalPath(pathWithQuery: string, requiredPrefix: "/portal/" | "/admin/"): boolean {
  if (!pathWithQuery.startsWith(requiredPrefix)) return false;
  if (pathWithQuery.includes("//")) return false;
  if (pathWithQuery.includes("\\")) return false;
  return true;
}

/**
 * Validates callbackUrl from auth query - internal portal/admin paths only.
 * Absolute URLs are allowed when the host is a trusted portal/admin deployment.
 */
export function parseSafeAuthCallbackUrl(raw: string | null): string | null {
  if (!raw) return null;

  try {
    const decoded = decodeURIComponent(raw.trim());
    if (!decoded) return null;

    if (decoded.startsWith("http://") || decoded.startsWith("https://")) {
      const url = new URL(decoded);
      if (!isTrustedAuthCallbackHostname(url.hostname)) return null;

      const pathWithQuery = `${url.pathname}${url.search}`;
      if (
        !isSafeInternalPath(pathWithQuery, "/portal/") &&
        !isSafeInternalPath(pathWithQuery, "/admin/")
      ) {
        return null;
      }

      return pathWithQuery;
    }

    const pathWithQuery = decoded.startsWith("/") ? decoded : `/${decoded}`;

    if (
      !isSafeInternalPath(pathWithQuery, "/portal/") &&
      !isSafeInternalPath(pathWithQuery, "/admin/")
    ) {
      return null;
    }

    return pathWithQuery;
  } catch {
    return null;
  }
}

/** @deprecated Use parseSafeAuthCallbackUrl - kept for existing imports */
export function parseSafePortalCallbackUrl(raw: string | null): string | null {
  const safe = parseSafeAuthCallbackUrl(raw);
  if (!safe?.startsWith("/portal/")) return null;
  return safe;
}

export function getPostAuthRedirectPath(
  callbackUrlParam: string | null,
  fallback: string = PageTypePortal.Dashboard,
): string {
  return parseSafeAuthCallbackUrl(callbackUrlParam) ?? fallback;
}

export function parseClassIdFromAuthCallback(
  callbackUrlParam: string | null,
): number | null {
  const safe = parseSafePortalCallbackUrl(callbackUrlParam);
  if (!safe) return null;
  return parseClassIdFromRegistrationPath(safe);
}
